/**
 * RS / History — horizontal hero world.
 *
 * The world moves three ways: 1:1 under the finger while dragging (no lerp,
 * no perceived latency), an iOS-like decaying fling after release, and a lerp
 * toward `targetX` for wheel / arrow keys / rail jumps. Input: horizontal
 * wheel (vertical scroll passes through to the page), pointer drag with a
 * directional lock (vertical gestures scroll the page; once locked the
 * pointer is captured so nothing else can steal the gesture), and arrow keys
 * while the hero is on screen. Everything count-dependent (max travel, progress
 * translation, rail cycle) is derived from the rendered panels, never
 * hardcoded.
 *
 * Performance: the rAF loop runs only while the section intersects the
 * viewport, and every layout read (panel width, rail row geometry) is cached
 * in #measure() — the tick performs writes only.
 */
import { FLING_DECAY_PER_MS, FLING_STOP_PX_PER_MS } from 'rs-drag-physics';

const RAIL_AUTO_PX_PER_SEC = 13;
const RAIL_GAP_PX = 18;
const RAIL_COPIES = 3;
const HINT_HIDE_AT_PX = 24;
const DRAG_LOCK_PX = 6;
const RAIL_DRAG_LOCK_PX = 10;

class RsHistory extends HTMLElement {
  #raf = null;
  #lastTime = 0;
  #observer = null;
  #visible = false;

  connectedCallback() {
    this.viewport = this.querySelector('[data-rs-viewport]');
    this.track = this.querySelector('[data-rs-track]');
    if (!this.viewport || !this.track || this.track.children.length === 0) return;

    this.panelCount = this.track.children.length;

    // A single panel has nothing to glide — only its video needs driving
    if (this.panelCount < 2) {
      this.activeIdx = 0;
      this.panelVideos = Array.from(this.track.children).map((panel) => Array.from(panel.querySelectorAll('video')));
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          this.#visible = entry.isIntersecting;
          this.#updateVideos();
        }
      });
      this.#observer.observe(this);
      return;
    }
    this.scrollWrap = this.querySelector('[data-rs-scroll]');
    this.forced = this.dataset.forcedScroll === 'true' && !!this.scrollWrap;
    this.swipeEnabled = this.dataset.swipe !== 'false';
    this.#findScroller();
    this.progress = this.querySelector('[data-rs-progress]');
    this.hint = this.querySelector('[data-rs-hint]');
    this.rail = this.querySelector('[data-rs-rail]');
    this.railInner = this.rail ? this.rail.querySelector('[data-rs-rail-inner]') : null;
    this.railRows = this.rail ? Array.from(this.rail.querySelectorAll('[data-rs-rail-row]')) : [];
    this.speedFactor = parseFloat(this.dataset.speedFactor) || 1.6;
    this.dragLockPx = parseFloat(this.dataset.dragLock) || DRAG_LOCK_PX;
    const inertia = parseFloat(this.dataset.inertia);
    this.inertia = Number.isNaN(inertia) ? 1 : inertia;

    this.targetX = 0;
    this.curX = 0;
    this.activeIdx = 0;
    this.hintHidden = false;
    this.railOffset = 0;
    this.railHover = false;
    this.railDrag = false;
    this.suppressRailClick = false;

    // Per-panel Shopify-hosted videos: preload="none" in markup, playback
    // driven here — only the active panel plays, everything else stays paused.
    this.panelVideos = Array.from(this.track.children).map((panel) => Array.from(panel.querySelectorAll('video')));
    this.#updateVideos();

    this.#measure();
    this.onResize = () => {
      this.#findScroller();
      this.#measure();
    };
    window.addEventListener('resize', this.onResize);

    this.#wireViewport();
    if (this.rail && this.railRows.length) this.#wireRail();

    this.onKey = (event) => {
      if (!this.#visible) return;
      if (event.target.closest('input, textarea, select, [contenteditable]')) return;
      const w = this.panelW;
      if (event.key === 'ArrowRight') {
        this.flingV = 0;
        this.#seek((Math.round(this.targetX / w) + 1) * w, true);
      } else if (event.key === 'ArrowLeft') {
        this.flingV = 0;
        this.#seek((Math.round(this.targetX / w) - 1) * w, true);
      }
    };
    window.addEventListener('keydown', this.onKey);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.#visible = entry.isIntersecting;
        if (entry.isIntersecting) {
          this.#start();
        } else {
          this.#stop();
        }
        this.#updateVideos();
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('pointermove', this.onPanMove);
    window.removeEventListener('pointerup', this.onPanUp);
    window.removeEventListener('pointercancel', this.onPanUp);
  }

  /* Desktop scrolls .page-wrapper (html/body are overflow:hidden >=990px),
     mobile scrolls the document. The container CHANGES when the viewport
     crosses 990px without a reload (theme-editor device preview, iPad
     rotation, window resize): re-detected on every resize, never latched. */
  #findScroller() {
    this.scroller = null;
    let anc = this.parentElement;
    while (anc && anc !== document.body) {
      const oy = getComputedStyle(anc).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && anc.scrollHeight > anc.clientHeight) {
        this.scroller = anc;
        break;
      }
      anc = anc.parentElement;
    }
  }

  /* All layout reads live here; the tick only consumes cached values. */
  #measure() {
    this.panelW = this.viewport.clientWidth;
    this.maxX = (this.panelCount - 1) * this.panelW;
    this.targetX = this.#clamp(this.targetX);
    if (this.scrollWrap) {
      const rect = this.scrollWrap.getBoundingClientRect();
      const base = this.scroller ? this.scroller.getBoundingClientRect().top : 0;
      this.wrapperTop = rect.top - base + this.#scrollPos();
      this.scrollRange = Math.max(1, this.scrollWrap.offsetHeight - this.viewport.offsetHeight);
    }

    if (this.railInner && this.railRows.length) {
      const rowHeight = this.railRows[0].offsetHeight;
      this.railRowStep = rowHeight + RAIL_GAP_PX;
      this.railRowHalf = rowHeight / 2;
      this.railCycle = this.railRowStep * (this.railRows.length / RAIL_COPIES);
      this.railViewHalf = this.rail.clientHeight / 2;
    }
  }

  #clamp(x) {
    return Math.max(0, Math.min(this.maxX, x));
  }

  #scrollPos() {
    return this.scroller ? this.scroller.scrollTop : window.scrollY;
  }

  #scrollToPos(top, smooth) {
    // 'instant' is mandatory: the theme sets scroll-behavior: smooth on the
    // scroll containers, so a plain scrollTo would animate — successive
    // per-frame writes from drag/fling/wheel cancel each other and the
    // horizontal gesture stalls
    const opts = smooth ? { top, behavior: 'smooth' } : { top, behavior: 'instant' };
    if (this.scroller) this.scroller.scrollTo(opts);
    else window.scrollTo(opts);
  }

  /* Scroll position that corresponds to a world offset */
  #scrollForX(x) {
    if (!this.scrollWrap || this.maxX <= 0) return this.#scrollPos();
    return this.wrapperTop + (x / this.maxX) * this.scrollRange;
  }

  /* Single entry point for every input: forced mode writes the scroll
     position (the source of truth), free mode writes the target directly */
  #seek(x, smooth) {
    x = this.#clamp(x);
    if (this.forced) this.#scrollToPos(this.#scrollForX(x), smooth);
    else this.targetX = x;
  }

  #wireViewport() {
    const el = this.viewport;

    el.addEventListener(
      'wheel',
      (event) => {
        if (event.target.closest('[data-rs-rail]')) return;
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        const d = event.deltaX;
        if (d > 0 && this.targetX >= this.maxX - 2) return;
        if (d < 0 && this.targetX <= 2) return;
        event.preventDefault();
        this.flingV = 0;
        this.#seek(this.targetX + d * this.speedFactor);
      },
      { passive: false }
    );

    // Horizontal swipe OFF: the pointer listeners are never registered —
    // wheel, arrow keys and the rail remain the only inputs
    if (!this.swipeEnabled) return;

    // Drag with directional lock: clearly vertical gestures scroll the page
    el.addEventListener('pointerdown', (event) => {
      if (event.target.closest('[data-rs-rail]')) return;
      this.panning = true;
      this.panLocked = false;
      // Touching the world catches it: any running fling stops under the finger
      this.flingV = 0;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      this.panStartTargetX = this.targetX;
      this.panPointerId = event.pointerId;
      this.panVelocity = 0;
      this.lastPanTime = 0;
      this.lastPanX = event.clientX;
    });
    window.addEventListener('pointermove', (this.onPanMove = (event) => {
      if (!this.panning) return;
      const dx = event.clientX - this.panStartX;
      const dy = event.clientY - this.panStartY;
      if (!this.panLocked) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > this.dragLockPx) {
          this.panning = false;
          return;
        }
        if (Math.abs(dx) > this.dragLockPx) {
          this.panLocked = true;
          // Once ours, the gesture stays ours: capture keeps neighbouring
          // UI (progress line, sections below) from stealing the pointer
          try {
            this.viewport.setPointerCapture(this.panPointerId);
          } catch {
            // pointer already released
          }
        } else {
          return;
        }
      }
      this.#seek(this.panStartTargetX - dx);
      // Flick velocity in px/ms, lightly smoothed against event jitter
      const now = performance.now();
      if (this.lastPanTime) {
        const dtMs = now - this.lastPanTime;
        if (dtMs > 0) {
          const v = (this.lastPanX - event.clientX) / dtMs;
          this.panVelocity = this.panVelocity * 0.6 + v * 0.4;
        }
      }
      this.lastPanTime = now;
      this.lastPanX = event.clientX;
    }));
    window.addEventListener('pointerup', (this.onPanUp = () => {
      if (this.panning && this.panLocked) {
        // Hand the flick over to the decaying fling in the tick
        this.flingV = this.panVelocity * this.inertia;
      }
      this.panning = false;
      this.panVelocity = 0;
      this.lastPanTime = 0;
    }));
    window.addEventListener('pointercancel', this.onPanUp);
  }

  #wireRail() {
    const rail = this.rail;

    rail.addEventListener('mouseenter', () => (this.railHover = true));
    rail.addEventListener('mouseleave', () => (this.railHover = false));

    rail.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.railOffset += event.deltaY;
      },
      { passive: false }
    );

    rail.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      this.railDrag = true;
      this.suppressRailClick = false;
      this.railPointerId = event.pointerId;
      this.railDragStart = event.clientY;
      this.railDragStartOffset = this.railOffset;
    });
    rail.addEventListener('pointermove', (event) => {
      if (!this.railDrag) return;
      const dy = event.clientY - this.railDragStart;
      if (!this.suppressRailClick && Math.abs(dy) > RAIL_DRAG_LOCK_PX) {
        this.suppressRailClick = true;
        try {
          rail.setPointerCapture(this.railPointerId);
        } catch {
          // pointer already released
        }
        rail.classList.add('is-dragging');
      }
      if (this.suppressRailClick) this.railOffset = this.railDragStartOffset - dy;
    });
    const endRailDrag = () => {
      this.railDrag = false;
      rail.classList.remove('is-dragging');
      setTimeout(() => (this.suppressRailClick = false), 50);
    };
    rail.addEventListener('pointerup', endRailDrag);
    rail.addEventListener('pointercancel', endRailDrag);

    rail.addEventListener('click', (event) => {
      if (this.suppressRailClick) return;
      const row = event.target.closest('[data-rs-rail-row]');
      if (!row) return;
      this.flingV = 0;
      this.#seek(parseInt(row.dataset.panelIndex, 10) * this.panelW, true);
    });
  }

  #setActiveIdx(idx) {
    this.activeIdx = idx;
    for (const row of this.railRows) {
      row.classList.toggle('is-active', parseInt(row.dataset.panelIndex, 10) === idx);
    }
    this.#updateVideos();
  }

  /* One download only: the source is chosen via matchMedia BEFORE src is
     assigned, so the unused variant is never requested (media attributes on
     <source> are unreliable for this) */
  #assignVideoSource(video) {
    if (!video || video.dataset.rsAssigned) return;
    const mobile = window.matchMedia('(max-width: 760px)').matches;
    const src = (mobile && video.dataset.srcMobile) || video.dataset.srcDesktop;
    const poster = (mobile && video.dataset.posterMobile) || video.dataset.posterDesktop;
    if (poster) video.poster = poster;
    if (src) video.src = src;
    video.dataset.rsAssigned = 'true';
  }

  #updateVideos() {
    for (let i = 0; i < this.panelVideos.length; i++) {
      for (const video of this.panelVideos[i]) {
        const shouldPlay = this.#visible && i === this.activeIdx;
        if (shouldPlay && video.paused) {
          this.#assignVideoSource(video);
          video.muted = true;
          const playing = video.play();
          if (playing && playing.catch) playing.catch(() => {});
        } else if (!shouldPlay && !video.paused) {
          video.pause();
        }
      }
    }
  }

  #start() {
    if (this.#raf) return;
    this.#lastTime = performance.now();
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop() {
    cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #tick = (time) => {
    const dt = Math.min((time - this.#lastTime) / 1000, 0.05);
    this.#lastTime = time;

    // The page scroll is the single source of truth for the world position:
    // native vertical scroll (wheel, trackpad, swipe) scrubs the panels while
    // the viewport is stuck; every other input writes the scroll position
    if (this.forced && this.maxX > 0) {
      const sp = Math.min(1, Math.max(0, (this.#scrollPos() - this.wrapperTop) / this.scrollRange));
      this.targetX = sp * this.maxX;
    }
    if (this.panning && this.panLocked) {
      this.curX = this.targetX;
    } else if (this.flingV) {
      const want = this.#clamp(this.targetX + this.flingV * dt * 1000);
      if (this.forced) this.#scrollToPos(this.#scrollForX(want));
      this.targetX = want;
      this.curX = want;
      this.flingV *= Math.pow(FLING_DECAY_PER_MS, dt * 1000);
      const atEdge = want <= 0 || want >= this.maxX;
      if (atEdge || Math.abs(this.flingV) < FLING_STOP_PX_PER_MS) this.flingV = 0;
    } else {
      const alpha = 1 - Math.pow(1 - 0.22, dt * 60);
      this.curX += (this.targetX - this.curX) * alpha;
      if (Math.abs(this.targetX - this.curX) < 0.1) this.curX = this.targetX;
    }
    this.track.style.transform = `translate3d(${-this.curX}px, 0, 0)`;

    if (this.progress && this.maxX > 0) {
      const p = this.curX / this.maxX;
      this.progress.style.transform = `translateX(${p * (this.panelCount - 1) * 100}%)`;
    }

    if (!this.hintHidden && this.curX > HINT_HIDE_AT_PX && this.hint) {
      this.hintHidden = true;
      this.hint.style.opacity = '0';
    }

    const idx = Math.round(this.curX / this.panelW);
    if (idx !== this.activeIdx) this.#setActiveIdx(idx);

    // Rail: vertical loop + 3D perspective from cached geometry
    if (this.railInner && this.railCycle > 0) {
      if (!this.railDrag && !this.railHover) {
        this.railOffset += RAIL_AUTO_PX_PER_SEC * dt;
      }
      this.railOffset = ((this.railOffset % this.railCycle) + this.railCycle) % this.railCycle;
      this.railInner.style.transform = `translateY(${-this.railOffset}px)`;

      for (let k = 0; k < this.railRows.length; k++) {
        const center = k * this.railRowStep + this.railRowHalf - this.railOffset;
        const d = (center - this.railViewHalf) / this.railViewHalf;
        const t = Math.max(0, 1 - Math.min(Math.abs(d), 1));
        this.railRows[k].style.transform =
          `translateZ(${t * 80}px) rotateX(${-d * 26}deg) scale(${0.8 + t * 0.32})`;
      }
    }

    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('rs-history')) {
  customElements.define('rs-history', RsHistory);
}
